import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import DatePickerButton from '../ui/date-picker-button';
import { TrendingUp, RefreshCw, Activity, Calendar, Zap } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import moment from 'moment';
const PerformanceTimeSeriesChart = ({
  title = "Active Power (KW) vs POA Irradiance (KW/m²)",
  startDate: propStartDate,
  endDate: propEndDate,
  selectedKPI
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(propStartDate || yesterday);
  const [endDate, setEndDate] = useState(propEndDate || yesterday);
  const [isLive] = useState(true);
  
  // Update dates when props change
  useEffect(() => {
    if (propStartDate) setStartDate(propStartDate);
    if (propEndDate) setEndDate(propEndDate);
  }, [propStartDate, propEndDate]);
  const {
    toast
  } = useToast();
  useEffect(() => {
    fetchTimeSeriesData();
  }, [startDate, endDate]);
  const fetchTimeSeriesData = async () => {
    try {
      setIsLoading(true);
      const startDateTime = startDate + ' 00:00:00';
      const endDateTime = endDate + ' 23:59:59';
      console.log('Performance Time Series: Fetching data with params:', {
        startDateTime,
        endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.DP_ACTIVE_POWER_ID, API_CONFIG.WS_LIVE_POA_POWER_DENSITY_ID],
        tags_list: ['DP_ACTIVE_POWER', 'WS_LIVE_POA_POWER_DENSITY']
      });

      // Fetch OMP data for Active Power and Irradiance
      const response = await apiService.getOmpDataByTags({
        start_date: startDateTime,
        end_date: endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.DP_ACTIVE_POWER_ID, API_CONFIG.WS_LIVE_POA_POWER_DENSITY_ID],
        tags_list: ['DP_ACTIVE_POWER', 'WS_LIVE_POA_POWER_DENSITY']
      });
      console.log('Performance Time Series: API Response:', response);
      if (response.data && response.data.json_object) {
        const processedData = [];
        console.log('Performance Time Series: JSON Object:', response.data.json_object);
        const jsonArray = Array.isArray(response.data.json_object) ? response.data.json_object : [];
        jsonArray.forEach(item => {
          if (item.ts) {
            const timestamp = new Date(item.ts).toLocaleString();
            const activePower = item.DP_PANEL_DP_ACTIVE_POWER || 0;
            const irradiance = item['WMS-02_WS_LIVE_POA_POWER_DENSITY'] || 0;
            processedData.push({
              timestamp,
              activePower: Number(activePower),
              irradiance: Number(irradiance)
            });
          }
        });
        console.log('Performance Time Series: Processed Data:', processedData);

        // Sort by timestamp
        processedData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        setData(processedData);
      }
    } catch (error) {
      console.error('Error fetching time series data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch time series data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const formatNumber = num => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {formatDate(label)}
          </p>
          {payload.map((entry, index) => <p key={index} className="text-sm" style={{
          color: entry.color
        }}>
              {entry.name}: {formatNumber(entry.value)} {entry.name.includes('Power') ? 'kW' : 'kW/m²'}
            </p>)}
        </div>;
    }
    return null;
  };
  const CustomLegend = ({
    payload
  }) => {
    return <div className="flex justify-center space-x-6 mt-4">
        {payload?.map((entry, index) => <div key={index} className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color
        }}></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {entry.value}
            </span>
          </div>)}
      </div>;
  };
  return <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            <span>{title}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            {isLive && <div className="flex items-center space-x-2 bg-green-100 dark:bg-green-900/20 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-800 dark:text-green-200">LIVE</span>
              </div>}
            <Button variant="outline" size="sm" onClick={fetchTimeSeriesData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Date Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <DatePickerButton selectedDate={startDate} onDateChange={date => setStartDate(date)} label="Start Date" placeholder="Select Start Date" />
          </div>
          <div>
            <DatePickerButton selectedDate={endDate} onDateChange={date => setEndDate(date)} label="End Date" placeholder="Select End Date" />
          </div>
        </div>

        {/* Chart */}
        {isLoading ? <div className="space-y-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-64 w-full" />
          </div> : data.length > 0 ? <div className="space-y-4">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={data} margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0
          }}>
                <defs>
                  <linearGradient id="activePowerGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#176BEF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#176BEF" stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="irradianceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F7885D" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#F7885D" stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="timestamp" tickFormatter={formatDate} tick={{
              fontSize: 12
            }} angle={-45} textAnchor="end" height={80} />
                <YAxis yAxisId="left" tick={{
              fontSize: 12
            }} label={{
              value: 'Active Power (kW)',
              angle: -90,
              position: 'insideLeft'
            }} />
                <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 12
            }} label={{
              value: 'POA Irradiance (kW/m²)',
              angle: 90,
              position: 'insideRight'
            }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Area type="monotone" dataKey="activePower" stroke="#176BEF" strokeWidth={2} fill="url(#activePowerGradient)" yAxisId="left" name="Active Power (kW)" />
                <Area type="monotone" dataKey="irradiance" stroke="#F7885D" strokeWidth={2} fill="url(#irradianceGradient)" yAxisId="right" name="Irradiance (kW/m²)" />
              </AreaChart>
            </ResponsiveContainer>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Zap className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Max Power
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {formatNumber(Math.max(...data.map(d => d.activePower)))} kW
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-5 w-5 text-orange-500" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Max Irradiance
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {formatNumber(Math.max(...data.map(d => d.irradiance)))} kW/m²
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Power
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {formatNumber(data.reduce((sum, d) => sum + d.activePower, 0) / data.length)} kW
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Data Points
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {data.length}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div> : <div className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No time series data available</p>
            <p className="text-sm text-gray-400 mt-2">Try selecting a different date range</p>
          </div>}
      </CardContent>
    </Card>;
};
export default PerformanceTimeSeriesChart;